import { type ITextureCreationOptions, Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Deferred } from '@babylonjs/core/Misc/deferred';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Scene } from '@babylonjs/core/scene';
import type { Nullable } from '@babylonjs/core/types';
import { type ITexture, TextureColorSpace } from '@seddi/surface-material';
import type { FileLoader } from '@seddi/ui-babylon-core';

export class TextureLoader {
  /** Babylon scene. */
  private readonly scene: Scene;
  /** File loader. */
  private readonly fileLoader: FileLoader;

  /**
   *
   * @param scene - Scene or engine the textures will belong to.
   * @param fileLoader-
   */
  constructor(scene: Scene, fileLoader: FileLoader) {
    this.scene = scene;
    this.fileLoader = fileLoader;
  }

  /**
   * Loads a texture.
   * @param data - Texture data.
   */
  loadAsync(data: ITexture): Promise<Texture> {
    return this._loadAsync(null, data);
  }

  /**
   * Loads a texture with asset container.
   * @param container - Babylon asset container.
   * @param data - Texture data.
   */
  loadAssetContainerAsync(container: Nullable<AssetContainer>, data: ITexture) {
    return this._loadAsync(container, data);
  }

  /**
   * Loads a texture with asset container.
   * @param scene - Babylon scene.
   * @param container - Babylon asset container.
   * @param data - Texture data.
   */
  private _loadAsync(container: Nullable<AssetContainer>, data: ITexture): Promise<Texture> {
    const promises = new Array<Promise<any>>();
    const deferred = new Deferred<void>();

    const textureCreationOptions: ITextureCreationOptions = {
      invertY: true,
      // loaderOptions: textureLoaderOptions,
      // mimeType: image.mimeType,
      // noMipmap: samplerData.noMipMaps,
      // samplingMode: samplerData.samplingMode,
      onLoad: () => {
        deferred.resolve();
      },
      onError: (message?: string, exception?: Error) => {
        deferred.reject(
          new Error(
            `${data.href}: ${
              exception?.message ? exception.message : message || 'Failed to load texture'
            }`,
          ),
        );
      },
      useSRGBBuffer: data.colorSpace === TextureColorSpace.GAMMA,
    };

    this.scene._blockEntityCollection = Boolean(container);
    const texture = new Texture(null, this.scene, textureCreationOptions);
    texture._parentContainer = container;
    this.scene._blockEntityCollection = false;
    promises.push(deferred.promise);

    promises.push(
      this.fileLoader.loadUriAsync(`/textures/${data.id}`, data.href).then(raw => {
        texture.updateURL(data.href, raw);
      }),
    );

    // texture.gammaSpace = data.colorSpace === TextureColorSpace.GAMMA;
    // texture.wrapU = samplerData.wrapU;
    // texture.wrapV = samplerData.wrapV;

    return Promise.all(promises).then(() => {
      return texture;
    });
  }
}
